import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

import { DateDomainService } from '@/domain/services';
import { TABLET_API_ORIGIN } from '@/global/env';
import { RestClientInterface } from '@/lib/restClient';
import { TempHourListResponse } from '@/services/TempHour/hooks/useTempHoursAPI';

export type TempHour = {
  id: string;
  start_time: string;
  end_time: string;
  is_closed: boolean;
  delivery_stores: DeliveryStore[];
};

export type DeliveryStore = {
  id: string;
  name: string;
};

const displayDateFormat = 'yyyy/MM/dd（E）';
const displayHoursFormat = 'HH:mm';

export const SITE_ID_QUERY_PARAM = 'site_id';

export function formatTempHour(tempHour: TempHour): string {
  const startDate = DateDomainService.parseNoColonFormat(tempHour.start_time);
  const tempHourDate = format(startDate, displayDateFormat, { locale: ja });
  if (tempHour.is_closed) {
    return `${tempHourDate} 全日休業`;
  }
  const startHour = format(startDate, displayHoursFormat);
  const endDate = DateDomainService.parseNoColonFormat(tempHour.end_time);
  const endHour = format(endDate, displayHoursFormat);
  return `${tempHourDate} ${startHour}〜${endHour}`;
}

interface Interface {
  list: (siteId: string) => Promise<TempHourListResponse>;
  get: (id: string) => Promise<TempHour>;
}

export class TempHourService implements Interface {
  constructor(private readonly restClient: RestClientInterface) {}

  public async list(siteId: string): Promise<TempHourListResponse> {
    return await this.restClient.get<TempHourListResponse>(
      `${TABLET_API_ORIGIN}/dashboard-api/sites/${siteId}/temp-hours` as const
    );
  }

  public async get(id: string): Promise<TempHour> {
    return await this.restClient.get<TempHour>(
      `${TABLET_API_ORIGIN}/dashboard-api/temp-hours/${id}` as const
    );
  }

  public async create(
    siteId: string,
    startTime: string,
    endTime: string,
    isClosed: boolean,
    deliveryStoreIds: string[]
  ): Promise<void> {
    const body = {
      start_time: startTime,
      end_time: endTime,
      is_closed: isClosed,
      delivery_store_ids: deliveryStoreIds,
    };
    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/sites/${siteId}/temp-hours` as const,
      body
    );
  }

  public async update(
    id: string,
    startTime: string,
    endTime: string,
    isClosed: boolean,
    deliveryStoreIds: string[]
  ): Promise<void> {
    const body = {
      start_time: startTime,
      end_time: endTime,
      is_closed: isClosed,
      delivery_store_ids: deliveryStoreIds,
    };
    return await this.restClient.patch(
      `${TABLET_API_ORIGIN}/dashboard-api/temp-hours/${id}` as const,
      body
    );
  }

  public async delete(id: string): Promise<void> {
    return await this.restClient.delete(
      `${TABLET_API_ORIGIN}/dashboard-api/temp-hours/${id}` as const
    );
  }
}
